// [TRANSECTS]: an NC row (Manning's n) precedes each transect's own X1 row.
// Real files restate NC before every transect even when unchanged, so this
// app treats it as the transect's own property rather than the toolkit's
// internal "carries forward until changed" parsing state — reading it as
// carry-forward state would attribute the wrong roughness to a transect
// whose own NC row was accidentally omitted.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export interface TransectStation {
  elevation: number;
  station: number;
}

export interface Transect {
  name: string;
  nLeft: number;
  nRight: number;
  nChannel: number;
  xLeftBank: number;
  xRightBank: number;
  lengthFactor: number;
  stationMultiplier: number;
  elevationOffset: number;
  stations: TransectStation[];
}

export function readTransects(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Transect[] {
  const rows = raw.get("TRANSECTS") ?? [];
  const transects: Transect[] = [];
  let pendingN = { nLeft: 0, nRight: 0, nChannel: 0 };
  let current: Transect | null = null;

  for (const row of rows) {
    const keyword = row[0]?.toUpperCase();
    if (keyword === "NC") {
      pendingN = {
        nLeft: parseNum(row[1], 0, "[TRANSECTS] NC nLeft", warnings),
        nRight: parseNum(row[2], 0, "[TRANSECTS] NC nRight", warnings),
        nChannel: parseNum(row[3], 0, "[TRANSECTS] NC nChannel", warnings),
      };
    } else if (keyword === "X1" && row[1]) {
      if (current) transects.push(current);
      const ctx = (field: string) => `[TRANSECTS] "${row[1]}" X1 ${field}`;
      current = {
        name: row[1],
        nLeft: pendingN.nLeft,
        nRight: pendingN.nRight,
        nChannel: pendingN.nChannel,
        xLeftBank: parseNum(row[3], 0, ctx("Xleft"), warnings),
        xRightBank: parseNum(row[4], 0, ctx("Xright"), warnings),
        lengthFactor: parseNum(row[7], 0, ctx("Lfactor"), warnings),
        stationMultiplier: parseNum(row[8], 0, ctx("Wfactor"), warnings),
        elevationOffset: parseNum(row[9], 0, ctx("Eoffset"), warnings),
        stations: [],
      };
    } else if (keyword === "GR" && current) {
      const name = current.name;
      for (let i = 1; i + 1 < row.length; i += 2) {
        current.stations.push({
          elevation: parseNum(row[i], 0, `[TRANSECTS] "${name}" GR elevation`, warnings),
          station: parseNum(row[i + 1], 0, `[TRANSECTS] "${name}" GR station`, warnings),
        });
      }
    }
  }
  if (current) transects.push(current);
  return transects;
}

export function writeTransects(
  raw: ReadonlyMap<string, string[][]>,
  transects: Transect[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  for (const t of transects) {
    rows.push(["NC", String(t.nLeft), String(t.nRight), String(t.nChannel)]);
    rows.push([
      "X1",
      t.name,
      String(t.stations.length),
      String(t.xLeftBank),
      String(t.xRightBank),
      "0",
      "0",
      String(t.lengthFactor),
      String(t.stationMultiplier),
      String(t.elevationOffset),
    ]);
    for (let i = 0; i < t.stations.length; i += 5) {
      const row = ["GR"];
      for (const s of t.stations.slice(i, i + 5)) row.push(String(s.elevation), String(s.station));
      rows.push(row);
    }
  }
  return writeOrDeleteSection(raw, "TRANSECTS", rows);
}

export function nextAvailableTransectName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "TR");
}
